function parseUrl(value) {
  return new URL(value, 'https://hewn-earth.invalid');
}

export function colourwayTitle(productTitle, colourValue) {
  const title = String(productTitle || '').trim();
  const colour = String(colourValue || '').trim();
  return colour ? `${title} — ${colour}` : title;
}

function serialiseUrl(url, original) {
  if (/^https?:\/\//i.test(original)) return url.toString();
  return `${url.pathname}${url.search}${url.hash}`;
}

export const WALL_ART_STYLES = Object.freeze([
  'Abstract',
  'Art Nouveau',
  'Boho',
  'Chinoiserie',
  'Coastal',
  'Cottagecore',
  'Folk Art',
  'Geometric',
  'Japandi',
  'Maximalist',
  'Mediterranean',
  'Mid-Century Modern',
  'Minimalist',
  'Modern Organic',
  'Pop Art',
  'Street Art',
  'Whimsical',
  'Line Art',
]);

const WALL_ART_STYLE_ALIASES = Object.freeze({
  'Abstract Art': 'Abstract',
  Bohemian: 'Boho',
  Cottage: 'Cottagecore',
  Cubist: 'Geometric',
  'Graffiti / Street Art-inspired': 'Street Art',
  'Mid-Century': 'Mid-Century Modern',
  'Naïve / Playful Illustration': 'Whimsical',
  'Pop & Graphic': 'Pop Art',
  'Warm Minimalist': 'Minimalist',
});

const WALL_ART_STYLE_REMOVE_ONLY = Object.freeze(new Set([
  'Chinese Inspired',
  'Collage & Cut-out',
  'Contemporary',
  'Modern Farmhouse',
  'Ornamental',
  'Painterly',
]));

// Keep the legacy Subject metafield private while migrating old links and forms.
function normaliseWallArtStyleParams(params) {
  const subject = 'filter.p.m.custom.subject';
  const style = 'filter.p.m.custom.styles';
  const subjectValues = params.getAll(subject);
  const legacyGeometricSubject = subjectValues.some((value) => value.split(',').includes('Geometric'));
  let changed = legacyGeometricSubject;
  if (legacyGeometricSubject) {
    params.delete(subject);
    for (const value of subjectValues) {
      const retained = value.split(',').filter((item) => item !== 'Geometric').join(',');
      if (retained) params.append(subject, retained);
    }
  }

  const styleValues = params.getAll(style).flatMap((value) => value.split(',')).filter(Boolean);
  const consolidated = [];
  for (const value of styleValues) {
    const replacement = WALL_ART_STYLE_ALIASES[value];
    if (replacement) changed = true;
    if (WALL_ART_STYLE_REMOVE_ONLY.has(value)) {
      changed = true;
      continue;
    }
    const next = replacement || value;
    if (!consolidated.includes(next)) consolidated.push(next);
    else if (next !== value || styleValues.filter((item) => item === value).length > 1) changed = true;
  }
  if (legacyGeometricSubject && !consolidated.includes('Geometric')) consolidated.push('Geometric');
  if (changed) {
    params.delete(style);
    if (consolidated.length) params.append(style, consolidated.join(','));
  }
  if (changed) params.delete('page');
  return changed;
}

function wallArtUsesStyle(url, params = url.searchParams) {
  return !/\/collections\/rugs(?:\/|$)/.test(url.pathname)
    && !params.getAll('filter.p.product_type').some((value) => value.split(',').includes('Rug'));
}

export function normaliseWallArtStyleUrl(value) {
  const url = parseUrl(value);
  if (!wallArtUsesStyle(url)) return value;
  return normaliseWallArtStyleParams(url.searchParams) ? serialiseUrl(url, value) : value;
}

export function normaliseGeometricUrl(value) {
  return normaliseWallArtStyleUrl(value);
}

function normaliseTagParams(params) {
  const values = [...new Set(params.getAll('filter.p.tag').flatMap((value) => value.split(',')).filter(Boolean))];
  params.delete('filter.p.tag');
  values.forEach((value) => params.append('filter.p.tag', value));
}

export function urlWithSort(value, sortBy) {
  const url = parseUrl(normaliseGeometricUrl(value));
  normaliseTagParams(url.searchParams);
  url.searchParams.set('sort_by', sortBy);
  url.searchParams.delete('page');
  return serialiseUrl(url, value);
}

export function filterIdentity(parameter, value) {
  return `${parameter}\0${value}`;
}

export function successfulFilterEntries(form) {
  return [...new FormData(form).entries()]
    .map(([name, value]) => [String(name), String(value)])
    .filter(([name, value]) => name !== 'page' && value.trim() !== '');
}

export function cleanFilterDestination({ entries, defaultSort, routes }) {
  const meaningful = entries.filter(([name, value]) => !(
    name === 'sort_by' && (value === '' || value === defaultSort)
  ));
  const unique = [...new Map(meaningful.map((entry) => [filterIdentity(...entry), entry])).values()];
  if (unique.length !== 1) return null;
  if (unique[0][0] === 'filter.p.m.custom.subject') return null;
  return routes.get(filterIdentity(...unique[0])) || null;
}

export function syntheticRemoveUrl(current, base) {
  const currentUrl = parseUrl(current);
  const baseUrl = parseUrl(base);
  baseUrl.search = currentUrl.search;
  normaliseTagParams(baseUrl.searchParams);
  baseUrl.searchParams.delete('page');
  return serialiseUrl(baseUrl, base);
}

export function urlWithSyntheticSort({ current, base, synthetic, sortBy }) {
  if (synthetic?.[0] === 'filter.p.m.custom.styles' && synthetic[1] === 'Geometric') {
    // Preserve the canonical Collection AND any additional selected Style.
    return urlWithSort(current, sortBy);
  }
  const target = parseUrl(normaliseGeometricUrl(synthetic ? base : current));
  if (synthetic) {
    target.search = parseUrl(normaliseGeometricUrl(current)).search;
    const selected = target.searchParams.getAll(synthetic[0]).flatMap((value) => value.split(','));
    if (!selected.includes(synthetic[1])) target.searchParams.append(synthetic[0], synthetic[1]);
  }
  normaliseTagParams(target.searchParams);
  target.searchParams.set('sort_by', sortBy);
  target.searchParams.delete('page');
  return serialiseUrl(target, synthetic ? base : current);
}

export function removeFilterValue(value, parameter, selectedValue) {
  const url = parseUrl(normaliseGeometricUrl(value));
  normaliseTagParams(url.searchParams);
  if (wallArtUsesStyle(url) && parameter === 'filter.p.m.custom.subject' && selectedValue === 'Geometric') {
    parameter = 'filter.p.m.custom.styles';
  }
  const retained = url.searchParams
    .getAll(parameter)
    .flatMap((item) => item.split(','))
    .filter((item) => item !== selectedValue && item !== '');
  url.searchParams.delete(parameter);
  if (parameter === 'filter.p.tag') retained.forEach((item) => url.searchParams.append(parameter, item));
  else if (retained.length > 0) url.searchParams.set(parameter, retained.join(','));
  url.searchParams.delete('page');
  return serialiseUrl(url, value);
}

export function designerFilterState(current, choices, syntheticTag = '') {
  const tags = new Set(parseUrl(current).searchParams.getAll('filter.p.tag').flatMap((value) => value.split(',')).filter(Boolean));
  const allowed = new Set(choices.map(({ value }) => value));
  const preserved = new Set([...tags].filter((value) => !allowed.has(value)));
  if (syntheticTag && !allowed.has(syntheticTag)) preserved.add(syntheticTag);
  return { selected: choices.filter(({ value }) => tags.has(value)), preserved: [...preserved] };
}

export function removeFilterParameters(current, parameters) {
  const url = parseUrl(normaliseGeometricUrl(current));
  normaliseTagParams(url.searchParams);
  parameters.forEach((parameter) => url.searchParams.delete(parameter));
  url.searchParams.delete('page');
  return serialiseUrl(url, current);
}

function bindDesignerFilter(root) {
  const facet = root.querySelector('[data-designer-filter]');
  const form = root.querySelector('[data-listing-filter-form]');
  if (!facet || !form) return;
  const inputs = [...facet.querySelectorAll('[data-designer-label]')];
  const choices = inputs.map((input) => ({ value: input.value, label: input.dataset.designerLabel }));
  const syntheticTag = form.dataset.syntheticFilterParam === 'filter.p.tag' ? form.dataset.syntheticFilterValue : '';
  const state = designerFilterState(window.location.href, choices, syntheticTag);
  const selected = new Set(state.selected.map(({ value }) => value));
  inputs.forEach((input) => {
    input.checked = selected.has(input.value);
    input.closest('label')?.classList.toggle('is-active', input.checked);
  });
  const summary = facet.querySelector('[data-designer-summary]');
  if (summary) summary.textContent = `Artist${selected.size ? ` · ${selected.size}` : ''}`;

  form.querySelectorAll('[data-preserved-tag-filter]').forEach((input) => input.remove());
  state.preserved.forEach((value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'filter.p.tag';
    input.value = value;
    input.setAttribute('data-preserved-tag-filter', '');
    form.append(input);
  });

  const allowed = new Set(choices.map(({ value }) => value));
  root.querySelectorAll('[data-active-filter-parameter]').forEach((link) => {
    const { activeFilterParameter: parameter, activeFilterValue: value } = link.dataset;
    if (parameter === 'filter.p.tag' && allowed.has(value)) link.remove();
    else link.setAttribute('href', removeFilterValue(window.location.href, parameter, value));
  });
  root.querySelectorAll('[data-active-filter-range]').forEach((link) => {
    link.setAttribute('href', removeFilterParameters(window.location.href, link.dataset.activeFilterRange.split('|')));
  });
  let chips = root.querySelector('.listing__active-filters');
  if (state.selected.length && !chips) {
    chips = document.createElement('div');
    chips.className = 'listing__active-filters';
    chips.setAttribute('aria-label', 'Active filters');
    root.querySelector('.listing__filter-form')?.append(chips);
  }
  state.selected.forEach(({ value, label }) => {
    const link = document.createElement('a');
    link.href = removeFilterValue(window.location.href, 'filter.p.tag', value);
    link.dataset.activeFilterParameter = 'filter.p.tag';
    link.dataset.activeFilterValue = value;
    link.textContent = `${label} `;
    const close = document.createElement('span');
    close.setAttribute('aria-hidden', 'true');
    close.textContent = '×';
    link.append(close);
    chips.append(link);
  });
  if (state.selected.length || state.preserved.length) root.querySelector('.listing__clear')?.classList.remove('is-hidden');
}

export function clearFiltersUrl(value) {
  const url = parseUrl(value);
  return url.pathname;
}

export function listingColumns(value) {
  return Number(value) === 3 ? 3 : 4;
}

export function selectedVariantMedia(variant, fallback) {
  return { url: variant?.url || fallback?.url || '' };
}

export function createInfinitePageController({
  initialNextUrl,
  loadPage,
  appendPage,
  onState = () => {},
}) {
  let nextUrl = normaliseGeometricUrl(String(initialNextUrl || ''));
  let loading = false;
  let complete = nextUrl === '';
  const loadedUrls = new Set();

  return {
    get nextUrl() { return nextUrl; },
    get isLoading() { return loading; },
    get isComplete() { return complete; },
    async loadNext() {
      if (loading || complete || nextUrl === '') return false;

      const requestedUrl = nextUrl;
      loading = true;
      onState('loading');

      try {
        const page = await loadPage(requestedUrl);
        if (!page || !Array.isArray(page.primary) || !Array.isArray(page.secondary)) {
          throw new Error('Invalid infinite-scroll page payload');
        }

        const followingUrl = normaliseGeometricUrl(String(page.nextUrl || ''));
        if (followingUrl && (followingUrl === requestedUrl || loadedUrls.has(followingUrl))) {
          throw new Error('Infinite-scroll pagination loop detected');
        }

        appendPage(page);
        loadedUrls.add(requestedUrl);
        nextUrl = followingUrl;
        complete = nextUrl === '';
        onState(complete ? 'complete' : 'idle');
        return true;
      } catch (error) {
        onState('error', error);
        return false;
      } finally {
        loading = false;
      }
    },
  };
}

function bindFacets(root) {
  const facets = [...root.querySelectorAll('[data-listing-facet]')];
  for (const facet of facets) {
    facet.addEventListener('toggle', () => {
      if (!facet.open) return;
      for (const other of facets) if (other !== facet) other.open = false;
    });
  }
  const outsideClick = (event) => {
    if (root.contains(event.target) && event.target.closest('[data-listing-facet]')) return;
    for (const facet of facets) facet.open = false;
  };
  document.addEventListener('click', outsideClick);
  return () => document.removeEventListener('click', outsideClick);
}

function bindSort(root) {
  const select = root.querySelector('[data-listing-sort]');
  if (!select) return;
  const form = root.querySelector('[data-listing-filter-form]');
  select.addEventListener('change', () => {
    const synthetic = form?.dataset.syntheticFilterParam
      ? [form.dataset.syntheticFilterParam, form.dataset.syntheticFilterValue]
      : null;
    window.location.assign(urlWithSyntheticSort({
      current: window.location.href,
      base: form?.dataset.filterBaseUrl || window.location.pathname,
      synthetic,
      sortBy: select.value,
    }));
  });
}

function bindFilterForm(root) {
  const form = root.querySelector('[data-listing-filter-form]');
  if (!form) return;
  form.addEventListener('formdata', (event) => {
    if (wallArtUsesStyle(parseUrl(form.action || window.location.href), event.formData)) {
      normaliseWallArtStyleParams(event.formData);
    }
  });
  form.querySelectorAll('[data-filter-clean-url]').forEach((input) => {
    if (!input.dataset.filterParameter || !input.dataset.filterValue) return;
  });
  form.addEventListener('submit', (event) => {
    const routePairs = [...form.querySelectorAll('[data-filter-clean-url]')]
      .filter((input) => input.dataset.filterParameter && input.dataset.filterValue)
      .map((input) => [
        filterIdentity(input.dataset.filterParameter, input.dataset.filterValue),
        input.dataset.filterCleanUrl,
      ]);
    const destination = cleanFilterDestination({
      action: form.action,
      entries: successfulFilterEntries(form),
      defaultSort: form.dataset.defaultSort || '',
      routes: new Map(routePairs),
    });
    if (!destination) return;
    event.preventDefault();
    window.location.assign(destination);
  });
  root.querySelectorAll('[data-synthetic-remove]').forEach((link) => {
    link.setAttribute('href', syntheticRemoveUrl(
      window.location.href,
      link.dataset.syntheticRemove,
    ));
  });
}

function bindColumns(root) {
  const grid = root.querySelector('[data-listing-grid]');
  const buttons = [...root.querySelectorAll('[data-listing-columns]')];
  if (!grid || buttons.length === 0) return;

  let stored = 4;
  try { stored = listingColumns(window.localStorage.getItem('listingColumns')); } catch {}
  const update = (value) => {
    const columns = listingColumns(value);
    grid.style.setProperty('--listing-columns', String(columns));
    buttons.forEach((button) => {
      const active = Number(button.dataset.listingColumns) === columns;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    try { window.localStorage.setItem('listingColumns', String(columns)); } catch {}
  };
  buttons.forEach((button) => button.addEventListener('click', () => update(button.dataset.listingColumns)));
  update(stored);
}

function bindCardColours(root) {
  root.querySelectorAll('[data-product-handle]').forEach((card) => {
    const main = card.querySelector('.listing-card__main');
    const title = card.querySelector('[data-card-title]');
    const dots = [...card.querySelectorAll('[data-colour-value]')];
    if (!main || dots.length === 0) return;
    const fallback = { url: main.getAttribute('href') || '' };

    const select = (dot) => {
      const selected = selectedVariantMedia(
        { url: dot.dataset.variantUrl },
        fallback,
      );
      main.setAttribute('href', selected.url);
      if (title) title.textContent = colourwayTitle(title.dataset.productTitle, dot.dataset.colourValue);
      const mediaTemplate = dot.querySelector('[data-variant-media]');
      const mediaFrame = card.querySelector('[data-card-media-frame]');
      if (mediaTemplate && mediaFrame) {
        mediaFrame.querySelectorAll('.listing-card__image, .listing-card__placeholder').forEach((image) => image.remove());
        mediaFrame.prepend(mediaTemplate.content.cloneNode(true));
      }
      dots.forEach((item) => {
        const active = item === dot;
        item.classList.toggle('is-selected', active);
        if (mediaTemplate) item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        select(dot);
      });
      dot.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        select(dot);
      });
    });
  });
}

function parseInfinitePage(nextDocument, listingType) {
  const nextRoot = nextDocument.querySelector(`[data-collection-listing][data-listing-type="${listingType}"]`);
  const nextLoader = nextRoot?.querySelector('[data-infinite-scroll]');
  if (!nextRoot || !nextLoader) throw new Error('Infinite-scroll response did not match the current listing');

  return {
    primary: [...(nextRoot.querySelector('[data-infinite-primary]')?.children || [])],
    secondary: [...nextRoot.querySelectorAll('[data-infinite-secondary] > a')],
    pagination: nextRoot.querySelector('[data-listing-pagination]'),
    nextUrl: nextLoader.dataset.nextUrl || '',
  };
}

export async function fetchListingDocument(url, signal) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    signal,
  });
  if (!response.ok) throw new Error(`Infinite-scroll request failed with ${response.status}`);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

async function fetchInfinitePage(url, listingType, signal) {
  return parseInfinitePage(await fetchListingDocument(url, signal), listingType);
}

function appendInfiniteNodes(container, nodes) {
  if (!container || nodes.length === 0) return 0;
  const fragment = document.createDocumentFragment();
  nodes.forEach((node) => fragment.append(node));
  bindCardColours(fragment);
  container.append(fragment);
  container.hidden = false;
  return nodes.length;
}

function bindInfiniteScroll(root) {
  const loader = root.querySelector('[data-infinite-scroll]');
  const primary = root.querySelector('[data-infinite-primary]');
  const sentinel = loader?.querySelector('[data-infinite-sentinel]');
  const status = loader?.querySelector('[data-infinite-status]');
  const retry = loader?.querySelector('[data-infinite-retry]');
  const listingType = root.dataset.listingType || '';
  const supported = typeof window.IntersectionObserver === 'function'
    && typeof window.fetch === 'function'
    && typeof window.DOMParser === 'function'
    && typeof window.AbortController === 'function';

  if (!loader || !primary || !sentinel || !status || !retry || !listingType || !supported) return;

  let observer;
  const setState = (state) => {
    loader.dataset.state = state;
    loader.hidden = false;
    retry.hidden = state !== 'error';
    root.classList.toggle('has-infinite-error', state === 'error');
    status.textContent = {
      idle: '',
      loading: 'Loading more pieces…',
      error: 'We couldn’t load more pieces.',
      complete: 'You’ve seen all pieces.',
    }[state] || '';

    if (!observer) return;
    observer.unobserve(sentinel);
    if (state === 'idle') observer.observe(sentinel);
    if (state === 'complete') observer.disconnect();
  };

  const abortController = new AbortController();
  const controller = createInfinitePageController({
    initialNextUrl: loader.dataset.nextUrl,
    loadPage: (url) => fetchInfinitePage(url, listingType, abortController.signal),
    appendPage: (page) => {
      appendInfiniteNodes(primary, page.primary);
      const secondary = root.querySelector('[data-infinite-secondary]');
      if (appendInfiniteNodes(secondary, page.secondary) > 0) {
        const group = root.querySelector('[data-search-resource-group]');
        if (group) group.hidden = false;
      }
      const currentPagination = root.querySelector('[data-listing-pagination]');
      if (currentPagination && page.pagination) currentPagination.replaceWith(page.pagination);
      loader.dataset.nextUrl = page.nextUrl;
    },
    onState: setState,
  });

  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) controller.loadNext();
  }, { rootMargin: '0px 0px 100% 0px' });

  retry.addEventListener('click', () => controller.loadNext());
  const onPageHide = event => { if (!event.persisted) abortController.abort(); };
  window.addEventListener('pagehide', onPageHide);
  root.classList.add('is-infinite-enabled');
  setState(controller.isComplete ? 'complete' : 'idle');
  return () => { observer.disconnect(); abortController.abort(); window.removeEventListener('pagehide', onPageHide); };
}

const listingDisposers = new WeakMap();
export function disposeListing(root) {
  listingDisposers.get(root)?.();
  listingDisposers.delete(root);
  delete root.dataset.listingBound;
}

export function initialiseListing(root) {
  if (root.dataset.listingBound === 'true') return;
  root.dataset.listingBound = 'true';
  const disposeFacets = bindFacets(root);
  bindDesignerFilter(root);
  bindFilterForm(root);
  bindSort(root);
  bindColumns(root);
  bindCardColours(root);
  const disposeInfinite = bindInfiniteScroll(root);
  listingDisposers.set(root, () => { disposeFacets(); disposeInfinite?.(); });
}

function initialiseAllListings() {
  if (replaceLegacyGeometricLocation()) return;
  document.querySelectorAll('[data-collection-listing]').forEach(initialiseListing);
}

function replaceLegacyGeometricLocation() {
  const current = window.location.href;
  const destination = normaliseGeometricUrl(current);
  if (destination === current) return false;
  // A reload is needed: the server rendered the legacy Subject product set.
  window.location.replace(destination);
  return true;
}

if (typeof document !== 'undefined') {
  window.HewnCollectionListing = {fetchListingDocument, initialiseListing, disposeListing};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseAllListings);
  else initialiseAllListings();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) replaceLegacyGeometricLocation();
  });
  window.addEventListener('popstate', replaceLegacyGeometricLocation);
}
